import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import { SharedModule } from "../../../../shared/shared.module";
import { CommonModule, DecimalPipe } from "@angular/common";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { SubZoneType, VALEUR_PAR_FAMILLE_DATA, ValeurGroupBy, ValeurStockDetail } from "../../models/data.model";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { ExcelExportButtonComponent } from "../../../../shared/components/excel-export-button/excel-export-button.component";
import { exportValeurStockExcel } from "./valeur-stock-excel.utils";
import { DateLimiteDisplayComponent } from "../../../../shared/components/date-limite-display/date-limite-display.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";

export interface ValeurParFamilleDialogData {
  title: string;
  items: { denomination: string; total: number }[];
  details: ValeurStockDetail[];
  total: number;
  groupBy: ValeurGroupBy;
  colorMap: Record<string, string>;
  fullData: VALEUR_PAR_FAMILLE_DATA;
}

interface LotRow {
  lotNumero: string;
  quantite: number;
  unite: string;
  total: number;
  entrepot: string;
  coutUnitaire: number;
  dluo: string | null;
}

interface ArticleRow {
  denomination: string;
  total: number;
  percentage: string;
  /** Quantité totale (somme des lots). Un même article a toujours la même
   *  unité dans le référentiel, donc l'agrégat a du sens à ce niveau. */
  quantite: number;
  unite: string;
  lots: LotRow[];
}

/** Niveau intermédiaire — utilisé en mode `byFamille` (sous-famille) et
 *  en mode `byEntrepot` (catégorie de sous-zone : PDC / NC / expé / etc.).
 *  Pour les autres modes (article, sous-famille), `subGroups` reste vide
 *  et `articles` est rempli directement (3 niveaux). */
interface SubGroupRow {
  denomination: string;
  total: number;
  percentage: string;
  articles: ArticleRow[];
}

interface GroupRow {
  denomination: string;
  total: number;
  percentage: string;
  /** Mode `byFamille` : sous-familles. Mode `byEntrepot` : catégories de
   *  sous-zone (Pieds de chaîne, Zone NC, Zone expédition…). Sinon vide. */
  subGroups: SubGroupRow[];
  /** Mode `byFamille` : articles orphelins (sans sous-famille rattachée).
   *  Mode `byEntrepot` : articles directement sur l'entrepôt parent (pas
   *  dans une sous-zone) — rendus APRÈS les sous-zones, en bas. Modes
   *  `byArticle` / `bySousFamille` : articles directement sous le groupe. */
  articles: ArticleRow[];
}

/** Libellé affiché pour chaque catégorie de sous-zone d'entrepôt. Tri par
 *  ordre métier (PDC en premier, NC ensuite, expé/réception, puis flux
 *  exceptionnels). Le bucket `null` (= stock directement sur l'entrepôt
 *  parent) est traité à part et rendu en bas de l'entrepôt sous le libellé
 *  "Stock direct". */
const SUB_ZONE_LABELS: Record<Exclude<SubZoneType, null>, string> = {
  pdc: "Pieds de chaîne",
  nc: "Zone non conforme",
  expedition: "Zone d'expédition",
  reception: "Zone de réception",
  quarantaine: "Zone de quarantaine",
  transfert: "Zone de transfert",
  dechargement: "Zone de déchargement",
  autre: "Autres sous-zones",
};
const SUB_ZONE_ORDER: Array<Exclude<SubZoneType, null>> = [
  "pdc",
  "nc",
  "expedition",
  "reception",
  "quarantaine",
  "transfert",
  "dechargement",
  "autre",
];

interface Stats {
  totalFormatted: string;
  count: number;
  countLabel: string;
  lotCount: number;
  topName: string;
  topPct: string;
  pareto80Count: number;
  pareto80Pct: string;
  avgFormatted: string;
}

@Component({
  selector: "vl-valeur-par-famille-dialog",
  standalone: true,
  imports: [CommonModule, SharedModule, HeaderDialogComponent, DecimalPipe, MatIcon, MatIconButton, MatTooltip, RadioCardComponent, ExcelExportButtonComponent, DateLimiteDisplayComponent, ChipsToggleComponent],
  templateUrl: "./valeur-par-famille-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValeurParFamilleDialogComponent {
  data = inject<ValeurParFamilleDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<ValeurParFamilleDialogComponent>);

  expanded = signal<Set<string>>(new Set());

  /** Pivot actif dans le dialog — initialisé sur celui du widget. L'utilisateur
   *  peut basculer entre Famille / Article / Entrepôt depuis le header sans
   *  refermer la modal. */
  groupBy = signal<ValeurGroupBy>(this.data.groupBy);
  groupByElements = [
    { name: "Famille", value: "byFamille" as ValeurGroupBy },
    { name: "Article", value: "byArticle" as ValeurGroupBy },
    { name: "Entrepôt", value: "byEntrepot" as ValeurGroupBy },
  ];

  isGrouped = computed(() => this.groupBy() === "byFamille" || this.groupBy() === "byEntrepot");

  private groupLabels: Record<ValeurGroupBy, string> = {
    byFamille: "familles",
    bySousFamille: "sous-familles",
    byArticle: "articles",
    byEntrepot: "entrepôts",
  };

  private titleLabels: Record<ValeurGroupBy, string> = {
    byFamille: "Valeur stock — Famille",
    bySousFamille: "Valeur stock — Sous-famille",
    byArticle: "Valeur stock — Article",
    byEntrepot: "Valeur stock — Entrepôt",
  };

  title = computed(() => this.titleLabels[this.groupBy()]);

  /** Items + détails de la dimension active. La source `fullData` contient
   *  les 4 dimensions, on lit celle correspondant au signal `groupBy`. */
  private activeItems = computed(() => {
    const all = this.data.fullData?.[this.groupBy()] ?? [];
    return all.filter((d) => d.total > 0);
  });

  /** Total monétaire global (constant quel que soit le pivot). */
  private totalValue = computed(() => this.data.fullData?.total ?? this.data.total ?? 0);

  stats = computed<Stats>(() => {
    const items = [...this.activeItems()].sort((a, b) => b.total - a.total);
    const total = this.totalValue() || 1;
    const count = items.length;

    // Pareto 80/20
    let cumul = 0;
    let pareto80Count = 0;
    for (const item of items) {
      cumul += item.total;
      pareto80Count++;
      if (cumul >= total * 0.8) break;
    }

    const top = items[0];

    return {
      totalFormatted: WidgetHelperService.formatNumeric(total) + " €",
      count,
      countLabel: this.groupLabels[this.groupBy()],
      lotCount: this.data.details.length,
      topName: top?.denomination ?? "—",
      topPct: top ? ((top.total / total) * 100).toFixed(1) : "0",
      pareto80Count,
      pareto80Pct: count > 0 ? ((pareto80Count / count) * 100).toFixed(0) : "0",
      avgFormatted: count > 0 ? WidgetHelperService.formatNumeric(total / count) + " €" : "—",
    };
  });

  rows = computed<GroupRow[]>(() => {
    const total = this.totalValue() || 1;
    const groupBy = this.groupBy();
    const sorted = [...this.activeItems()].sort((a, b) => b.total - a.total);

    return sorted.map((item) => {
      // Filtrer les détails pour ce groupe (top niveau)
      const groupDetails = this.data.details.filter((d) => {
        if (groupBy === "byEntrepot") return d.entrepot === item.denomination;
        if (groupBy === "byFamille") return d.famille === item.denomination;
        if (groupBy === "bySousFamille") return d.sousFamille === item.denomination;
        if (groupBy === "byArticle") return d.article === item.denomination;
        return false;
      });

      // Mode byFamille : famille → (sous-famille → article → lot) +
      // (articles orphelins sans sous-famille → lot, directement sous la
      // famille). Pas de wrapper "Non classé" : un article qui n'a pas de
      // sous-famille rattachée est rendu directement sous la famille.
      if (groupBy === "byFamille") {
        const subMap = new Map<
          string,
          Map<string, { total: number; lots: LotRow[] }>
        >();
        const orphanMap = new Map<string, { total: number; lots: LotRow[] }>();

        for (const d of groupDetails) {
          const lot: LotRow = {
            lotNumero: d.lotNumero,
            quantite: d.quantite,
            unite: d.unite,
            total: d.total,
            entrepot: d.entrepot,
            coutUnitaire: d.coutUnitaire,
            dluo: d.dluo,
          };
          if (!d.sousFamille) {
            if (!orphanMap.has(d.article)) {
              orphanMap.set(d.article, { total: 0, lots: [] });
            }
            const entry = orphanMap.get(d.article)!;
            entry.total += d.total;
            entry.lots.push(lot);
          } else {
            if (!subMap.has(d.sousFamille)) subMap.set(d.sousFamille, new Map());
            const artMap = subMap.get(d.sousFamille)!;
            if (!artMap.has(d.article)) artMap.set(d.article, { total: 0, lots: [] });
            const entry = artMap.get(d.article)!;
            entry.total += d.total;
            entry.lots.push(lot);
          }
        }

        const subGroups: SubGroupRow[] = Array.from(subMap.entries())
          .map(([sfDenom, artMap]) => {
            const articles: ArticleRow[] = Array.from(artMap.entries())
              .map(([artDenom, v]) => ({
                denomination: artDenom,
                total: v.total,
                percentage: ((v.total / total) * 100).toFixed(1),
                quantite: v.lots.reduce((s, l) => s + (l.quantite || 0), 0),
                unite: v.lots[0]?.unite ?? "",
                lots: v.lots.sort((a, b) => b.total - a.total),
              }))
              .sort((a, b) => b.total - a.total);
            const sfTotal = articles.reduce((s, a) => s + a.total, 0);
            return {
              denomination: sfDenom,
              total: sfTotal,
              percentage: ((sfTotal / total) * 100).toFixed(1),
              articles,
            };
          })
          .sort((a, b) => b.total - a.total);

        const orphanArticles: ArticleRow[] = Array.from(orphanMap.entries())
          .map(([artDenom, v]) => ({
            denomination: artDenom,
            total: v.total,
            percentage: ((v.total / total) * 100).toFixed(1),
            quantite: v.lots.reduce((s, l) => s + (l.quantite || 0), 0),
            unite: v.lots[0]?.unite ?? "",
            lots: v.lots.sort((a, b) => b.total - a.total),
          }))
          .sort((a, b) => b.total - a.total);

        return {
          denomination: item.denomination,
          total: item.total,
          percentage: ((item.total / total) * 100).toFixed(1),
          subGroups,
          articles: orphanArticles,
        };
      }

      // Mode byEntrepot : 4 niveaux entrepôt → catégorie sous-zone (PDC,
      // Zone NC, expé…) → article → lot. Les stocks directement sur
      // l'entrepôt parent (subZoneType === null) sont regroupés sous une
      // catégorie "Stock direct" rendue en BAS, sous les sous-zones.
      if (groupBy === "byEntrepot") {
        const subZoneArtMap = new Map<
          Exclude<SubZoneType, null>,
          Map<string, { total: number; lots: LotRow[] }>
        >();
        const directArtMap = new Map<string, { total: number; lots: LotRow[] }>();

        for (const d of groupDetails) {
          const lot: LotRow = {
            lotNumero: d.lotNumero,
            quantite: d.quantite,
            unite: d.unite,
            total: d.total,
            entrepot: d.subEntrepot ?? d.entrepot,
            coutUnitaire: d.coutUnitaire,
            dluo: d.dluo,
          };
          if (d.subZoneType === null) {
            if (!directArtMap.has(d.article)) directArtMap.set(d.article, { total: 0, lots: [] });
            const entry = directArtMap.get(d.article)!;
            entry.total += d.total;
            entry.lots.push(lot);
          } else {
            const cat = d.subZoneType;
            if (!subZoneArtMap.has(cat)) subZoneArtMap.set(cat, new Map());
            const artMap = subZoneArtMap.get(cat)!;
            if (!artMap.has(d.article)) artMap.set(d.article, { total: 0, lots: [] });
            const entry = artMap.get(d.article)!;
            entry.total += d.total;
            entry.lots.push(lot);
          }
        }

        const subGroups: SubGroupRow[] = SUB_ZONE_ORDER.flatMap((cat) => {
          const artMap = subZoneArtMap.get(cat);
          if (!artMap || artMap.size === 0) return [];
          const articles: ArticleRow[] = Array.from(artMap.entries())
            .map(([artDenom, v]) => ({
              denomination: artDenom,
              total: v.total,
              percentage: ((v.total / total) * 100).toFixed(1),
              quantite: v.lots.reduce((s, l) => s + (l.quantite || 0), 0),
              unite: v.lots[0]?.unite ?? "",
              lots: v.lots.sort((a, b) => b.total - a.total),
            }))
            .sort((a, b) => b.total - a.total);
          const catTotal = articles.reduce((s, a) => s + a.total, 0);
          return [{
            denomination: SUB_ZONE_LABELS[cat],
            total: catTotal,
            percentage: ((catTotal / total) * 100).toFixed(1),
            articles,
          }];
        });

        // "Stock direct" : produits directement dans l'entrepôt parent
        // (pas dans une sous-zone). Rendu en dernier sous-groupe pour les
        // mettre visuellement APRÈS les sous-zones, comme demandé.
        if (directArtMap.size > 0) {
          const directArticles: ArticleRow[] = Array.from(directArtMap.entries())
            .map(([artDenom, v]) => ({
              denomination: artDenom,
              total: v.total,
              percentage: ((v.total / total) * 100).toFixed(1),
              quantite: v.lots.reduce((s, l) => s + (l.quantite || 0), 0),
              unite: v.lots[0]?.unite ?? "",
              lots: v.lots.sort((a, b) => b.total - a.total),
            }))
            .sort((a, b) => b.total - a.total);
          const directTotal = directArticles.reduce((s, a) => s + a.total, 0);
          subGroups.push({
            denomination: "Stock direct",
            total: directTotal,
            percentage: ((directTotal / total) * 100).toFixed(1),
            articles: directArticles,
          });
        }

        return {
          denomination: item.denomination,
          total: item.total,
          percentage: ((item.total / total) * 100).toFixed(1),
          subGroups,
          articles: [],
        };
      }

      // Modes bySousFamille / byArticle : 3 niveaux groupe → article → lot.
      const articleMap = new Map<string, { total: number; lots: LotRow[] }>();
      for (const d of groupDetails) {
        const artKey = groupBy === "byArticle" ? `${d.entrepot}` : d.article;
        if (!articleMap.has(artKey)) articleMap.set(artKey, { total: 0, lots: [] });
        const entry = articleMap.get(artKey)!;
        entry.total += d.total;
        entry.lots.push({
          lotNumero: d.lotNumero,
          quantite: d.quantite,
          unite: d.unite,
          total: d.total,
          entrepot: d.entrepot,
          coutUnitaire: d.coutUnitaire,
          dluo: d.dluo,
        });
      }
      const articles: ArticleRow[] = Array.from(articleMap.entries())
        .map(([denomination, v]) => ({
          denomination,
          total: v.total,
          percentage: ((v.total / total) * 100).toFixed(1),
          quantite: v.lots.reduce((s, l) => s + (l.quantite || 0), 0),
          unite: v.lots[0]?.unite ?? "",
          lots: v.lots.sort((a, b) => b.total - a.total),
        }))
        .sort((a, b) => b.total - a.total);

      return {
        denomination: item.denomination,
        total: item.total,
        percentage: ((item.total / total) * 100).toFixed(1),
        subGroups: [],
        articles,
      };
    });
  });

  toggle(key: string) {
    const current = new Set(this.expanded());
    if (current.has(key)) {
      current.delete(key);
    } else {
      current.add(key);
    }
    this.expanded.set(current);
  }

  isOpen(key: string): boolean {
    return this.expanded().has(key);
  }

  getColor(denomination: string): string | null {
    return this.data.colorMap?.[denomination] ?? null;
  }

  exporting = signal(false);

  async exportExcel() {
    this.exporting.set(true);
    try {
      await exportValeurStockExcel(this.data.fullData);
    } finally {
      this.exporting.set(false);
    }
  }
}
